import { SEO } from "@/components/shared/SEO";
import { BlogDetail } from "@/components/blog/BlogDetail";
import { Container } from "@/components/layout/Container";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { ArrowLeft, AlertCircle } from "lucide-react";
import { motion } from "framer-motion";
import { useParams, Link } from "wouter";
import { useQuery } from "@tanstack/react-query";
import type { Blog } from "@shared/types";

export default function BlogDetailPage() {
  const params = useParams();
  const blogId = params.id;

  const { data: blog, isLoading, error } = useQuery<Blog>({
    queryKey: [`/api/blogs/${blogId}`],
    enabled: !!blogId,
  });

  if (isLoading) {
    return (
      <>
        <SEO title="Loading..." description="記事を読み込み中..." />
        <Container className="py-8 md:py-16">
          <div className="max-w-4xl mx-auto space-y-6">
            {/* Back button skeleton */}
            <Skeleton className="h-6 w-32" />
            
            {/* Header skeleton */}
            <div className="space-y-4">
              <Skeleton className="h-64 md:h-96 w-full rounded-lg" />
              <Skeleton className="h-6 w-24" />
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-8 w-2/3" />
              <Skeleton className="h-4 w-48" />
            </div>
            
            {/* Content skeleton */}
            <div className="space-y-4 pt-8">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-4 w-full" />
              ))}
            </div>
          </div>
        </Container>
      </>
    );
  }

  if (error || !blog) {
    return (
      <>
        <SEO title="記事が見つかりません" description="指定された記事が見つかりませんでした。" />
        <Container className="py-16 md:py-24">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="max-w-3xl mx-auto text-center"
          >
            <AlertCircle className="w-16 h-16 mx-auto mb-4 text-muted-foreground" />
            <h1 className="text-2xl font-bold mb-4">記事が見つかりません</h1>
            <p className="text-muted-foreground mb-8">
              指定された記事が見つからないか、削除された可能性があります。
            </p>
            <Link href="/blog">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                ブログ一覧に戻る
              </Button>
            </Link>
          </motion.div>
        </Container>
      </>
    );
  }

  return (
    <>
      <SEO
        title={blog.title || 'ブログ記事'}
        description={blog.content ? blog.content.replace(/<[^>]*>/g, '').substring(0, 160) : 'ブログ記事の詳細ページです。'}
        keywords={`${blog.category?.name || 'ブログ'}, blog, tech`}
        ogImage={blog.eyecatch?.url}
      />
      <BlogDetail blog={blog} />
    </>
  );
}